import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Zap, Film, Megaphone, Clapperboard, Music, Smartphone, ArrowRight, Upload, X, Calculator,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl, track } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";

const ICONS = { film: Film, megaphone: Megaphone, clapperboard: Clapperboard, music: Music, smartphone: Smartphone };
const QUALITIES = [["preview", "Preview"], ["standard", "Standard"], ["production", "Production"], ["cinema", "Cinema"]];
const RES = ["480p", "720p", "1080p", "4k"];

const sel = "mt-2 w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[12.5px] outline-none focus:border-[#ff4b2b]/60";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mono text-[9.5px] uppercase tracking-[0.14em] text-[#8288a0]">{label}</span>
    {children}
  </label>
);

export default function QuickCreate() {
  const { locale } = useI18n();
  const fr = locale === "fr";
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [data, setData] = useState(null);
  const [theme, setTheme] = useState(null);
  const [est, setEst] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const fileRef = useRef(null);

  const [f, setF] = useState({
    subtemplate: "", idea: "", style_notes: "", shots: 4, shot_duration: 5,
    resolution: "720p", quality: "standard", aspect_ratio: "16:9", model_id: "",
    with_voice: false, with_music: false, storyboard_first: false,
  });

  useEffect(() => {
    api.get("/quick/themes").then(({ data: d }) => {
      setData(d);
      const t = d.themes.find((x) => x.code === params.get("theme")) || d.themes[0];
      setTheme(t);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecting a theme loads its defaults; the client can override everything after.
  useEffect(() => {
    if (!theme) return;
    setF((p) => ({
      ...p, shots: theme.shots, shot_duration: theme.shot_duration,
      aspect_ratio: theme.aspect_ratio, quality: theme.quality,
      subtemplate: theme.subtemplates?.[0]?.code || "",
    }));
  }, [theme]);

  const estimate = useCallback(async () => {
    if (!theme) return;
    try {
      const { data: e } = await api.post("/quick/estimate", {
        theme: theme.code, shots: Number(f.shots), shot_duration: Number(f.shot_duration),
        resolution: f.resolution, quality: f.quality, aspect_ratio: f.aspect_ratio,
        model_id: f.model_id || null, with_voice: f.with_voice,
        with_music: f.with_music, storyboard_first: f.storyboard_first,
      });
      setEst(e);
    } catch { /* keep the previous estimate on a transient error */ }
  }, [theme, f]);

  useEffect(() => {
    const t = setTimeout(estimate, 220);
    return () => clearTimeout(t);
  }, [estimate]);

  const ents = data?.entitlements;
  const engines = (data?.engines || []).filter((e) => !e.fallback_only);
  const activeEngine = engines.find((e) => e.id === f.model_id);
  const engineCap = activeEngine?.max_duration || 30;
  const planCap = ents?.max_shot_duration || 30;
  const durations = (activeEngine?.duration_values?.length ? activeEngine.duration_values : [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 30])
    .filter((d) => d <= Math.min(engineCap, planCap));
  const maxShots = ents ? Math.max(1, Math.floor(ents.max_film_duration / Math.max(2, Number(f.shot_duration)))) : 20;
  const allowedRes = ents ? RES.slice(0, RES.indexOf(ents.max_resolution) + 1) : RES;
  const allowedQ = ents ? QUALITIES.slice(0, QUALITIES.findIndex(([v]) => v === ents.max_quality) + 1) : QUALITIES;

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: a } = await api.post("/assets/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRef(a);
      toast.success(fr ? "Photo importée — elle servira de premier plan" : "Photo imported — it becomes the first shot");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const create = async () => {
    if (!theme || f.idea.trim().length < 3) return;
    setBusy(true);
    try {
      track("quick_create", { theme: theme.code, subtemplate: f.subtemplate });
      const { data: d } = await api.post("/quick/create", {
        theme: theme.code, subtemplate: f.subtemplate || null, idea: f.idea.trim(),
        style_notes: f.style_notes, reference_asset_id: ref?.id || null,
        shots: Number(f.shots), shot_duration: Number(f.shot_duration),
        resolution: f.resolution, quality: f.quality, aspect_ratio: f.aspect_ratio,
        model_id: f.model_id || null, with_voice: f.with_voice,
        with_music: f.with_music, storyboard_first: f.storyboard_first,
        language: locale,
      });
      for (const shot of d.shots) {
        try {
          await api.post("/generations", {
            kind: "video", prompt: shot.prompt, title: shot.title,
            duration: d.shot_duration, resolution: d.resolution,
            aspect_ratio: d.aspect_ratio, quality: d.quality, variations: 1,
            routing: "cost_saver", model_id: d.model_id,
            project_id: d.project.id, shot_id: shot.id, sequence_id: d.sequence.id,
            reference_asset_id: shot.reference_asset_ids?.[0] || null,
            generate_audio: false,
          });
        } catch (e) { toast.error(apiErr(e)); break; }
      }
      await refresh();
      toast.success(fr ? "Votre film part en génération" : "Your film is generating");
      nav(`/studio/projects/${d.project.id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="quick-create-page">
      <p className="overline">{fr ? "Création rapide" : "Quick Create"}</p>
      <h1 className="mt-3 max-w-[760px] font-display text-3xl font-semibold sm:text-4xl">
        {fr ? "Une vidéo finie sans ouvrir le studio complet" : "A finished video without opening the full studio"}
      </h1>
      <p className="mt-4 max-w-[640px] text-sm text-[#9194a6]">
        {fr ? "Choisissez un thème et un style, décrivez votre idée, réglez ce que vous voulez — le calculateur affiche le coût exact en crédits avant de lancer quoi que ce soit."
            : "Pick a theme and a style, describe your idea, tune whatever you want — the calculator shows the exact credit cost before anything is launched."}
      </p>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {(data?.themes || []).map((t) => {
          const Icon = ICONS[t.icon] || Film;
          const active = theme?.code === t.code;
          return (
            <button
              key={t.code} onClick={() => setTheme(t)} data-testid={`quick-theme-${t.code}`}
              className={`hot-border rounded-2xl border p-6 text-left transition-colors duration-300 ${
                active ? "border-[#ff4b2b]/55 bg-[#ff4b2b]/[0.07]" : "border-[#212230] bg-[#0b0b11] hover:bg-[#101118]"}`}
            >
              <Icon className={`h-5 w-5 ${active ? "text-[#ff4b2b]" : "text-[#9194a6]"}`} />
              <p className="mt-4 font-display text-[15px] font-medium">{fr ? t.label_fr : t.label_en}</p>
              <p className="mt-2 text-[12.5px] leading-relaxed text-[#9194a6]">{fr ? t.blurb_fr : t.blurb_en}</p>
              <p className="mono mt-4 text-[10.5px] text-[#00d5ff]">
                {t.shots} {fr ? "plans" : "shots"} × {t.shot_duration}s · {t.total_credits.toLocaleString()} cr
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-[1.45fr_1fr]">
        <div className="rounded-2xl border border-[#212230] bg-[#0b0b11] p-7">
          {theme?.subtemplates?.length > 0 && (
            <>
              <p className="overline">{fr ? "Style dans ce thème" : "Style in this theme"}</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="quick-subtemplates">
                {theme.subtemplates.map((s) => (
                  <button
                    key={s.code} onClick={() => setF({ ...f, subtemplate: s.code })}
                    data-testid={`quick-sub-${s.code}`}
                    className={`rounded-full border px-4 py-2 text-[12px] font-medium transition-colors duration-200 ${
                      f.subtemplate === s.code ? "border-[#ff4b2b]/60 bg-[#ff4b2b]/15 text-[#ffd7cb]" : "border-[#212230] text-[#9194a6] hover:text-white"}`}
                  >
                    {fr ? s.label_fr : s.label_en}
                  </button>
                ))}
              </div>
            </>
          )}

          <p className="overline mt-7">{fr ? "Votre idée" : "Your idea"}</p>
          <textarea
            value={f.idea} onChange={(e) => setF({ ...f, idea: e.target.value })} rows={3}
            data-testid="quick-idea"
            placeholder={fr ? "Ex. une marque de café artisanal torréfié à Marseille, ambiance chaleureuse au lever du jour"
                            : "e.g. an artisan coffee brand roasted in Marseille, warm sunrise mood"}
            className="mt-2 w-full resize-none rounded-lg border border-[#212230] bg-[#101118] px-4 py-3 text-[13.5px] outline-none focus:border-[#ff4b2b]/60"
          />

          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <Field label={fr ? "Durée par plan" : "Length per shot"}>
              <select className={sel} value={f.shot_duration} data-testid="quick-shot-duration"
                onChange={(e) => setF({ ...f, shot_duration: Number(e.target.value) })}>
                {durations.map((d) => <option key={d} value={d} children={`${d}s`} />)}
              </select>
            </Field>
            <Field label={fr ? "Nombre de plans" : "Number of shots"}>
              <input type="number" min={1} max={Math.min(20, maxShots)} className={sel}
                value={f.shots} data-testid="quick-shots"
                onChange={(e) => setF({ ...f, shots: Number(e.target.value) })} />
            </Field>
            <Field label={fr ? "Format" : "Format"}>
              <select className={sel} value={f.aspect_ratio} data-testid="quick-ratio"
                onChange={(e) => setF({ ...f, aspect_ratio: e.target.value })}>
                {(data?.ratios || ["16:9"]).map((r) => <option key={r} value={r} children={r} />)}
              </select>
            </Field>
            <Field label={fr ? "Résolution" : "Resolution"}>
              <select className={sel} value={f.resolution} data-testid="quick-resolution"
                onChange={(e) => setF({ ...f, resolution: e.target.value })}>
                {allowedRes.map((r) => <option key={r} value={r} children={r} />)}
              </select>
            </Field>
            <Field label={fr ? "Qualité" : "Quality"}>
              <select className={sel} value={f.quality} data-testid="quick-quality"
                onChange={(e) => setF({ ...f, quality: e.target.value })}>
                {allowedQ.map(([v, l]) => <option key={v} value={v} children={l} />)}
              </select>
            </Field>
            <Field label={fr ? "Moteur" : "Engine"}>
              <select className={sel} value={f.model_id} data-testid="quick-engine"
                onChange={(e) => setF({ ...f, model_id: e.target.value })}>
                <option value="">{fr ? "Le moins cher (auto)" : "Cheapest (auto)"}</option>
                {engines.map((e) => (
                  <option key={e.id} value={e.id} disabled={e.locked}
                    children={e.locked ? `${e.label} — ${fr ? "offre supérieure" : "upgrade"}` : e.label} />
                ))}
              </select>
            </Field>
          </div>

          <p className="overline mt-7">{fr ? "Options" : "Options"}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[["storyboard_first", fr ? "Storyboard d'abord (7× moins cher à l'essai)" : "Storyboard first (7× cheaper to try)"],
              ["with_voice", fr ? "Voix off française native" : "Native voice-over"],
              ["with_music", fr ? "Musique" : "Music"]].map(([k, label]) => (
              <button
                key={k} onClick={() => setF({ ...f, [k]: !f[k] })} data-testid={`quick-opt-${k}`}
                className={`rounded-full border px-4 py-2 text-[12px] font-medium transition-colors duration-200 ${
                  f[k] ? "border-[#00d5ff]/55 bg-[#00d5ff]/12 text-[#9beaff]" : "border-[#212230] text-[#9194a6] hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <p className="overline mt-7">{fr ? "Personnalisation" : "Personalisation"}</p>
          <textarea
            value={f.style_notes} onChange={(e) => setF({ ...f, style_notes: e.target.value })} rows={2}
            data-testid="quick-style-notes"
            placeholder={fr ? "Précisions libres : couleurs de marque, objectif, époque, interdits…"
                            : "Free direction: brand colours, lens, era, things to avoid…"}
            className="mt-2 w-full resize-none rounded-lg border border-[#212230] bg-[#101118] px-4 py-3 text-[12.5px] outline-none focus:border-[#ff4b2b]/60"
          />

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              data-testid="quick-photo-input" onChange={(e) => upload(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} data-testid="quick-photo-btn"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[12px] font-semibold">
              <Upload className="h-3.5 w-3.5" />
              {fr ? "Importer une photo (animée en 1er plan)" : "Import a photo (animated as shot 1)"}
            </button>
            {ref && (
              <span className="flex items-center gap-2 rounded-full border border-[#00d5ff]/40 bg-[#00d5ff]/10 px-3 py-1.5" data-testid="quick-photo-chip">
                <img src={fileUrl(ref.id)} alt="" className="h-6 w-6 rounded object-cover" />
                <span className="mono text-[10px] text-[#9beaff]">{fr ? "photo prête" : "photo ready"}</span>
                <button onClick={() => setRef(null)} data-testid="quick-photo-remove"><X className="h-3 w-3" /></button>
              </span>
            )}
          </div>
        </div>

        {/* Live calculator */}
        <div className="h-fit rounded-2xl border border-[#ff4b2b]/35 bg-[#ff4b2b]/[0.05] p-7 lg:sticky lg:top-6" data-testid="quick-calculator">
          <p className="flex items-center gap-2 font-display text-[15px] font-medium">
            <Calculator className="h-4 w-4 text-[#ff4b2b]" />
            {fr ? "Calculateur en direct" : "Live calculator"}
          </p>

          {est && (
            <>
              <p className="mono mt-4 text-[10.5px] uppercase tracking-[0.14em] text-[#8288a0]" data-testid="calc-engine">
                {est.engine.label} · {est.shots} × {est.shot_duration}s = {est.film_duration}s
                {est.film_duration >= 60 ? ` (${Math.round(est.film_duration / 60 * 10) / 10} min)` : ""}
              </p>

              <div className="mt-5 space-y-2" data-testid="calc-lines">
                {est.lines.map((ln) => (
                  <div key={ln.label} className="flex items-baseline justify-between gap-3 text-[12.5px]">
                    <span className="text-[#9194a6]">{ln.label}</span>
                    <span className="mono shrink-0 text-[#c0c3d0]">{ln.credits.toLocaleString()} cr</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-white/10 pt-5">
                <div className="flex items-baseline justify-between">
                  <span className="mono text-[10px] uppercase tracking-[0.16em] text-[#8288a0]">
                    {fr ? "Total" : "Total"}
                  </span>
                  <span className="flex items-center gap-2 font-display text-3xl font-semibold text-[#ff4b2b]" data-testid="calc-total">
                    <Zap className="h-5 w-5" />{est.total_credits.toLocaleString()}
                  </span>
                </div>
                <p className="mono mt-2 text-[10.5px] text-[#8288a0]" data-testid="calc-balance">
                  {fr ? "Solde" : "Balance"} {Number(user.credits).toLocaleString()} →{" "}
                  <span className={est.sufficient ? "text-[#00ff9d]" : "text-[#ff8a8a]"}>
                    {est.balance_after.toLocaleString()}
                  </span>
                </p>
              </div>

              {est.notices.length > 0 && (
                <ul className="mt-5 space-y-1.5" data-testid="calc-notices">
                  {est.notices.map((n) => (
                    <li key={n} className="text-[11px] leading-relaxed text-[#ffc76b]">• {n}</li>
                  ))}
                </ul>
              )}

              <button
                onClick={create}
                disabled={busy || f.idea.trim().length < 3 || !est.sufficient}
                data-testid="quick-create-submit"
                className="btn-amber mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[13px] font-semibold disabled:opacity-50"
              >
                {busy ? (fr ? "Création…" : "Creating…") : (fr ? "Créer ma vidéo" : "Create my video")}
                <ArrowRight className="h-4 w-4" />
              </button>

              {!est.sufficient && (
                <p className="mt-3 text-[11.5px] text-[#ff8a8a]" data-testid="quick-insufficient">
                  {fr ? "Crédits insuffisants — réduisez le nombre de plans, la résolution ou la qualité, ou ajoutez un pack."
                      : "Not enough credits — lower the shot count, resolution or quality, or add a pack."}
                </p>
              )}
              <p className="mt-4 text-[11px] leading-relaxed text-[#8288a0]">
                {fr ? "Le coût est débité au lancement et remboursé automatiquement si une génération échoue."
                    : "Credits are charged at launch and automatically refunded if a generation fails."}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
